import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Navbar from "./Navbar";
import SEO from "../components/SEO";

const SERVICE_GROUPS = [
  {
    category: "Combo & Value Packages",
    items: [
      "Full Legs + Full Hands + Underarms",
      "Full Face + Underarms",
      "Full Hands + Underarms",
      "Full Front + Full Back",
      "Bikini + Buttocks",
      "Full Face + Bikini",
    ],
  },
  {
    category: "Individual Treatment Areas",
    items: [
      "Underarms",
      "Full Face",
      "Neck",
      "Hands",
      "Legs",
      "Back",
      "Bikini",
      "Chest & Abdomen",
      "Buttocks",
    ],
  },
];

const TRUST_POINTS = [
  {
    title: "Zero Sales Pressure",
    desc: "A direct clinical assessment with an aesthetician to evaluate skin type, hair density, and session timelines.",
  },
  {
    title: "Patch Test Coordination",
    desc: "Arrange a complimentary spot test to experience our contact cooling diode firsthand.",
  },
  {
    title: "100% Confidential Consultation",
    desc: "Your photos, details, and treatment queries are strictly protected under internal safety protocols.",
  },
];

export default function RequestCall() {
  const [formData, setFormData] = useState({
    name: "",
    number: "",
    email: "",
    service: "",
    time: "",
    date: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const todayString = new Date().toISOString().split("T")[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const numberPattern = /^[6-9]\d{9}$/;
    const selectedDateTime = new Date(
      `${formData.date}T${formData.time || "00:00"}`,
    );
    const now = new Date();

    if (!numberPattern.test(formData.number)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Mobile Number",
        text: "Please provide a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9.",
        confirmButtonColor: "#8dcae4",
      });
      setLoading(false);
      return;
    }

    if (selectedDateTime < now) {
      Swal.fire({
        icon: "error",
        title: "Invalid Callback Window",
        text: "The requested callback date and time cannot be in the past.",
        confirmButtonColor: "#8dcae4",
      });
      setLoading(false);
      return;
    }

    const scriptURL =
      "https://script.google.com/macros/s/AKfycbyvsMcypP9XB-5uZlFA6CjtMy6KsdR4FwexMKcdmwl0meRun7XTYtjSal43Jxan1V07/exec";
    const form = new FormData();
    form.append("Name", formData.name);
    form.append("Number", formData.number);
    form.append("Email", formData.email);
    form.append("Services", formData.service);
    form.append("Date Time", `${formData.date} ${formData.time}`);

    try {
      const response = await fetch(scriptURL, { method: "POST", body: form });
      if (!response.ok) throw new Error("Submission network failure.");

      Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      }).fire({
        icon: "success",
        title: "Callback Request Received",
        text: "Our specialist will contact you at your requested time.",
      });

      setFormData({
        name: "",
        number: "",
        email: "",
        service: "",
        time: "",
        date: "",
      });
      setLoading(false);
      navigate("/home");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Scheduling Failed",
        text: "We were unable to route your request. Please message us on WhatsApp or call us directly.",
        confirmButtonColor: "#8dcae4",
      });
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#FBFBFA",
        color: "#1E293B",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      }}
      className="min-vh-100"
    >
      <SEO
        title="Request a Consultation Call | FlawSkin Laser Clinic"
        description="Schedule a confidential callback with FlawSkin's certified aesthetic practitioners. Discuss personalized diode laser treatment plans and custom packages."
        canonical="https://flawskin.com/request-call"
      />
      <Navbar />

      {/* Hero Header */}
      <section
        className="py-5 border-bottom border-light-subtle position-relative overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
        }}
      >
        <div className="container py-lg-4 text-center position-relative">
          <div
            className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-3 border"
            style={{ backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}
          >
            <span
              className="badge rounded-pill"
              style={{
                backgroundColor: "#8dcae4",
                color: "#FFFFFF",
                fontSize: "0.72rem",
              }}
            >
              1-ON-1 ADVICE
            </span>
            <span className="text-secondary small fw-medium">
              Direct Practitioner Access
            </span>
          </div>

          <h1
            className="display-5 fw-bolder tracking-tight mb-2"
            style={{ color: "#0F172A", letterSpacing: "-0.03em" }}
          >
            Request a{" "}
            <span style={{ color: "#8dcae4" }}>Clinical Callback</span>
          </h1>

          <p
            className="text-secondary mx-auto mb-0"
            style={{
              maxWidth: "620px",
              fontSize: "0.95rem",
              lineHeight: "1.6",
            }}
          >
            Tell us your desired treatment areas and preferred window. A
            certified practitioner will call you to walk through fluence
            settings, pricing, and safety prep.
          </p>
        </div>
      </section>

      {/* Main Structural Section */}
      <section className="py-5">
        <div className="container py-lg-3">
          <div className="row g-4 justify-content-between align-items-stretch">
            {/* Left Column: Context & Trust Markers */}
            <div className="col-12 col-lg-5 d-flex flex-column justify-content-between">
              <div>
                <span
                  className="text-uppercase fw-bold d-block mb-1"
                  style={{
                    fontSize: "0.72rem",
                    letterSpacing: "0.08em",
                    color: "#8dcae4",
                  }}
                >
                  Consultation Protocol
                </span>
                <h2 className="h4 fw-bold mb-3" style={{ color: "#0F172A" }}>
                  What happens during your consultation call?
                </h2>
                <p
                  className="text-secondary small mb-4"
                  style={{ lineHeight: "1.7" }}
                >
                  We believe in zero-surprise clinical care. Our callback is an
                  informative consultation focused on your dermatological
                  profile before any treatment commitment.
                </p>

                <div className="d-flex flex-column gap-3 mb-4">
                  {TRUST_POINTS.map((pt, i) => (
                    <div
                      key={i}
                      className="p-3.5 rounded-3 bg-white border shadow-xs"
                      style={{ borderColor: "#E2E8F0" }}
                    >
                      <div className="d-flex align-items-start gap-2.5">
                        <span
                          className="rounded-circle p-1 d-inline-flex mt-0.5"
                          style={{
                            backgroundColor: "#CCFBF1",
                            color: "#0F766E",
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        <div>
                          <div className="fw-bold small text-dark mb-0.5">
                            {pt.title}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.82rem", lineHeight: "1.5" }}
                          >
                            {pt.desc}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Urgent WhatsApp Quick-Card */}
              <div
                className="p-3.5 rounded-3 border d-flex align-items-center justify-content-between mt-auto"
                style={{ backgroundColor: "#F0FDF4", borderColor: "#BBF7D0" }}
              >
                <div>
                  <div className="fw-bold small" style={{ color: "#166534" }}>
                    Need instant answers?
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Skip the wait and text our support desk.
                  </div>
                </div>
                <a
                  href="https://wa.me/917892644030?text=Hi%20Flawskin%2C%20I%20have%20a%20quick%20question%20regarding%20treatments."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm text-white fw-semibold px-3 py-1.5 rounded-2"
                  style={{
                    backgroundColor: "#059669",
                    border: "none",
                    fontSize: "0.8rem",
                  }}
                >
                  WhatsApp Now
                </a>
              </div>
            </div>

            {/* Right Column: High-Conversion Form */}
            <div className="col-12 col-lg-7">
              <div
                className="card border rounded-4 p-4 p-md-5 bg-white shadow-sm"
                style={{ borderColor: "#E2E8F0" }}
              >
                <div className="mb-4 pb-2 border-bottom">
                  <h3 className="h5 fw-bold mb-1" style={{ color: "#0F172A" }}>
                    Select Your Callback Window
                  </h3>
                  <span className="text-muted small">
                    Fields marked with <span className="text-danger">*</span>{" "}
                    are mandatory.
                  </span>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Name Input */}
                  <div className="mb-3">
                    <label
                      htmlFor="name"
                      className="form-label fw-semibold small mb-1"
                      style={{ color: "#334155" }}
                    >
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 px-3 small border"
                      id="name"
                      name="name"
                      placeholder="e.g., Ananya Sharma"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={{ borderColor: "#CBD5E1", fontSize: "0.9rem" }}
                    />
                  </div>

                  {/* Contact Row */}
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-sm-6">
                      <label
                        htmlFor="number"
                        className="form-label fw-semibold small mb-1"
                        style={{ color: "#334155" }}
                      >
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span
                          className="input-group-text bg-light border text-muted small px-2.5"
                          style={{ borderColor: "#CBD5E1" }}
                        >
                          +91
                        </span>
                        <input
                          type="tel"
                          className="form-control rounded-end-3 py-2 px-3 small border"
                          id="number"
                          name="number"
                          placeholder="98765 43210"
                          value={formData.number}
                          onChange={handleChange}
                          maxLength={10}
                          required
                          style={{ borderColor: "#CBD5E1", fontSize: "0.9rem" }}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <label
                        htmlFor="email"
                        className="form-label fw-semibold small mb-1"
                        style={{ color: "#334155" }}
                      >
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control rounded-3 py-2 px-3 small border"
                        id="email"
                        name="email"
                        placeholder="ananya@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        style={{ borderColor: "#CBD5E1", fontSize: "0.9rem" }}
                      />
                    </div>
                  </div>

                  {/* Treatment Selection */}
                  <div className="mb-3">
                    <label
                      htmlFor="service"
                      className="form-label fw-semibold small mb-1"
                      style={{ color: "#334155" }}
                    >
                      Intended Treatment Area{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select rounded-3 py-2 px-3 small border"
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      style={{
                        borderColor: "#CBD5E1",
                        fontSize: "0.9rem",
                        color: formData.service ? "#0F172A" : "#64748B",
                      }}
                    >
                      <option value="">
                        Select a treatment area or package...
                      </option>
                      {SERVICE_GROUPS.map((grp, gIdx) => (
                        <optgroup key={gIdx} label={grp.category}>
                          {grp.items.map((srv, sIdx) => (
                            <option key={sIdx} value={srv}>
                              {srv}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* Date & Time Row */}
                  <div className="row g-3 mb-4">
                    <div className="col-12 col-sm-6">
                      <label
                        htmlFor="date"
                        className="form-label fw-semibold small mb-1"
                        style={{ color: "#334155" }}
                      >
                        Preferred Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control rounded-3 py-2 px-3 small border"
                        id="date"
                        name="date"
                        min={todayString}
                        value={formData.date}
                        onChange={handleChange}
                        required
                        style={{ borderColor: "#CBD5E1", fontSize: "0.9rem" }}
                      />
                    </div>
                    <div className="col-12 col-sm-6">
                      <label
                        htmlFor="time"
                        className="form-label fw-semibold small mb-1"
                        style={{ color: "#334155" }}
                      >
                        Preferred Time Window{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="time"
                        className="form-control rounded-3 py-2 px-3 small border"
                        id="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                        style={{ borderColor: "#CBD5E1", fontSize: "0.9rem" }}
                      />
                    </div>
                  </div>

                  {/* Submission CTA */}
                  <button
                    className="btn btn-lg w-100 fw-semibold rounded-3 py-2.5 shadow-sm d-flex align-items-center justify-content-center text-white"
                    type="submit"
                    disabled={loading}
                    style={{
                      backgroundColor: "#8dcae4",
                      border: "none",
                      transition: "background-color 0.2s ease",
                      fontSize: "0.95rem",
                    }}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Routing Request...
                      </>
                    ) : (
                      "Confirm & Schedule Callback"
                    )}
                  </button>

                  <div className="text-center mt-3">
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      🔒 We respect your privacy. No unsolicited promotional
                      spam or telemarketing.
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
